<script setup lang="ts">
import { initializeVaultBridge } from '@/components/vault/vault';
import LoaderView from '@ui/loader/LoaderView.vue';
import Modal from '@ui/modal/Modal.vue';
import Toastr from '@ui/toastr/Toastr.vue';
import { addTabRefreshEvent } from '@utils/helpers';
import Permissions from '@utils/Permissions';
import { onMounted, onUnmounted, ref } from 'vue';
import {
  appVm,
  getClinicCustomReports,
  getUnreadMessages,
  handleShortcutControlK,
  isLoading,
  isTrainingClinic,
  loadGlobals,
  systemUser,
  toggleMenu
} from './main-store';
import ClinicWarnings from './modules/ClinicWarnings.vue';
import SidebarNavigation from './modules/SidebarNavigation.vue';

const globalsLoading = ref(true);

onMounted(async () => {
  await loadGlobals();
  getUnreadMessages()
  globalsLoading.value = false;

  if (window.innerWidth < 1024) {
    appVm.toggleSideMenu = false;
  }

  if (systemUser.permissions.includes(Permissions.Reports.View)) {
    getClinicCustomReports()
  }

  document.documentElement.setAttribute("lang", systemUser?.clinic?.culture ?? "en-US")
  document.title = `${systemUser.clinic.clinicAbbr ?? systemUser.clinic.clinicName} | Clinic HQ`;

  window.addEventListener('keydown', handleShortcutControlK);
  addTabRefreshEvent();
  initializeVaultBridge();
})

onUnmounted(() => window.removeEventListener('keydown', handleShortcutControlK));

</script>
<template>
  <Toastr />
  <Modal />
  <LoaderView v-if="isLoading || globalsLoading" />
  <div class="min-h-full" v-if="!globalsLoading">
    <SidebarNavigation />

    <div class="sticky top-0 z-40 flex items-center gap-x-6 p-4 shadow-sm lg:hidden hidden-print"
      :class="isTrainingClinic ? 'bg-amber-40' : 'bg-brand-900'" v-if="appVm.layoutMode === 'sidebar'">
      <button type="button" class="-m-2.5 p-2.5 lg:hidden"
        :class="isTrainingClinic ? 'text-gray-900' : 'text-indigo-200'" @click="toggleMenu">
        <span class="sr-only">Open sidebar</span>
        <svg class="size-6" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"
          data-slot="icon">
          <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
        </svg>
      </button>
      <div class="flex-1 text-sm/6 font-semibold" :class="isTrainingClinic ? 'text-gray-900' : 'text-white'">
        {{ systemUser?.clinic?.clinicName }}
      </div>
    </div>

    <main class="min-h-full transition-[padding] duration-300"
      :class="{ 'lg:pl-72 2xl:pl-96': appVm.layoutMode === 'sidebar' && appVm.toggleSideMenu }">
      <div class="page-heading" v-if="appVm.defaultLayout">
        <span class="page-title">{{ appVm.title }}</span>
      </div>
      <div :class="{ 'page-body': appVm.defaultLayout }">
        <ClinicWarnings />
        <RouterView />
      </div>
    </main>
  </div>
</template>
